import * as React from 'react';

import { Button, Col, ConsoleDialog, Row } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideCheck } from 'lucide-react';

import { brokeredOrderCreateAtom } from '../../_graphql/mutationBrokeredOrderCreate';
import { brokeredOrderLinkOrdersAtom } from '../../_graphql/mutationBrokeredOrderLinkOrders';
import {
  $brokeredOrderForm,
  $resetBrokeredOrderForm,
} from '../../_state/brokeredOrderForm.state';
import * as state from '../../_state/brokeredOrdersList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { toLineItemCreateInput } from '../../utils/lineItemCalculations';

import { BrokeredOrderWizardStepContractInfo } from './BrokeredOrderWizardStepContractInfo';
import { BrokeredOrderWizardStepMaterials } from './BrokeredOrderWizardStepMaterials';

import { StepIndicator } from '~/modules/trade/_components/_shared/StepIndicator';

interface BrokeredOrderCreateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface Step {
  number: number;
  title: string;
}

function generateOrderNumber(): string {
  const now = new Date();
  const year = now.getFullYear().toString().slice(-2);
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const day = now.getDate().toString().padStart(2, '0');
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `BO-${year}${month}${day}-${random}`;
}

const STEP_COMPONENTS: Record<
  number,
  React.ComponentType<{ disabled?: boolean }>
> = {
  1: BrokeredOrderWizardStepContractInfo,
  2: BrokeredOrderWizardStepMaterials,
};

export const BrokeredOrderCreateDialog: React.FC<
  BrokeredOrderCreateDialogProps
> = ({ isOpen, onClose, onSuccess }) => {
  const t = useTranslations();
  const toast = useToast();

  const [currentStep, setCurrentStep] = React.useState(1);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const form = useAtomValue($brokeredOrderForm);
  const resetForm = useSetAtom($resetBrokeredOrderForm);
  const { refetch } = useAtomValue(state.$remoteBrokeredOrders);

  const createBrokeredOrder = useSetAtom(brokeredOrderCreateAtom({}));
  const linkOrders = useSetAtom(brokeredOrderLinkOrdersAtom({}));

  const wizardSteps: Step[] = React.useMemo(
    () => [
      {
        number: 1,
        title: t.orders.wizard.steps.contractInfo,
      },
      {
        number: 2,
        title: t.orders.wizard.steps.materials,
      },
    ],
    [t],
  );

  React.useEffect(() => {
    if (isOpen) {
      resetForm();
      setCurrentStep(1);
    }
  }, [isOpen, resetForm]);

  const handleClose = React.useCallback(() => {
    resetForm();
    setCurrentStep(1);
    onClose();
  }, [onClose, resetForm]);

  const handleBack = React.useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const handleNext = React.useCallback(() => {
    if (currentStep < wizardSteps.length) {
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep, wizardSteps.length]);

  const handleSubmit = React.useCallback(async () => {
    try {
      setIsSubmitting(true);

      const validLineItems = toLineItemCreateInput(form.lineItems);

      const result = await createBrokeredOrder({
        orderNumber: generateOrderNumber(),
        notes: form.notes.trim() || null,
        lineItems: validLineItems.length > 0 ? validLineItems : undefined,
      });

      const createdOrderId = result?.tradeBrokeredOrderCreate?.id;

      if (
        createdOrderId &&
        (form.purchaseOrderIds.length > 0 || form.salesOrderIds.length > 0)
      ) {
        await linkOrders({
          id: createdOrderId,
          addPurchaseOrderIds:
            form.purchaseOrderIds.length > 0
              ? form.purchaseOrderIds
              : undefined,
          addSalesOrderIds:
            form.salesOrderIds.length > 0 ? form.salesOrderIds : undefined,
        });
      }

      toast.success(t.orders.toast.created);
      refetch?.();
      onSuccess?.();
      handleClose();
    } catch (err) {
      toast.error(
        t.orders.toast.createError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    form,
    createBrokeredOrder,
    linkOrders,
    toast,
    refetch,
    onSuccess,
    handleClose,
    t.orders.toast,
  ]);

  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === wizardSteps.length;

  const StepComponent = STEP_COMPONENTS[currentStep];

  return (
    <ConsoleDialog
      className="@BrokeredOrderCreateDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      size="fill"
      title={
        <Col gap3>
          <Row gap3 className="text-xl font-semibold">
            <span>{t.orders.create.title}</span>
          </Row>
          <StepIndicator steps={wizardSteps} currentStep={currentStep} />
        </Col>
      }
      titleClassName="bg-foreground/5 pt-3"
      content={StepComponent ? <StepComponent disabled={isSubmitting} /> : null}
      footer={
        <div className="flex w-full items-center justify-between gap-4">
          <div>
            {!isFirstStep && (
              <Button
                color="neutral"
                variant="outline"
                onPress={handleBack}
                isDisabled={isSubmitting}
              >
                {t.orders.wizard.buttons.back}
              </Button>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              color="neutral"
              onPress={handleClose}
              isDisabled={isSubmitting}
            >
              {t.common.actions.cancel}
            </Button>
            {isLastStep ? (
              <Button
                icon={<LucideCheck />}
                color="primary"
                onPress={handleSubmit}
                isDisabled={isSubmitting}
                isPending={isSubmitting}
              >
                {isSubmitting
                  ? t.common.loading.creating
                  : t.orders.create.submitButton}
              </Button>
            ) : (
              <Button
                color="primary"
                variant="solid"
                onPress={handleNext}
                isDisabled={isSubmitting}
              >
                {t.orders.wizard.buttons.next}
              </Button>
            )}
          </div>
        </div>
      }
    />
  );
};
